import axios from "axios";
import { axiosLogger } from "../utils/logger";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const axiosInstance = axios.create({
  baseURL,
  withCredentials: true, // Required for cookies to be sent with requests
  headers: {
    "Content-Type": "application/json",
  },
});

// Token getter function - will be updated by AuthContext
let getCurrentToken = () => null;

// Function to update the token getter
export const setTokenGetter = (tokenGetter) => {
  getCurrentToken = tokenGetter;
};

// Request interceptor to attach the Firebase ID token
axiosInstance.interceptors.request.use(
  async (config) => {
    axiosLogger.log('📡 Axios request interceptor:', {
      url: config.url,
      method: config.method
    });
    
    const token = await getCurrentToken();
    if (token) {
      axiosLogger.log('🔑 Attaching Firebase ID token to request');
      config.headers["Authorization"] = `Bearer ${token}`;
    } else {
      axiosLogger.log('⚠️ No Firebase ID token available for request');
    }
    return config;
  },
  (error) => {
    axiosLogger.error('❌ Axios request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Setup response interceptor with Firebase token handling
export const setupInterceptors = (getCurrentTokenFn, authFailureHandler) => {
  // Track if interceptors are already set up
  let hasInterceptors = false;
  
  // Only set up interceptors once
  if (hasInterceptors) {
    return;
  }
  
  hasInterceptors = true;
  
  axiosInstance.interceptors.response.use(
    (response) => {
      axiosLogger.log('✅ Axios response successful:', {
        url: response.config.url,
        status: response.status
      });
      return response;
    },
    async (error) => {
      axiosLogger.log('❌ Axios response error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.response?.data?.detail || error.message
      });
      
      const originalRequest = error.config;

      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes("/api/v1/auth/login/") &&
        !originalRequest.url.includes("/api/v1/auth/register/")
      ) {
        axiosLogger.log('🔄 401 error detected, attempting auth failure handler...');
        originalRequest._retry = true;

        try {
          // For Firebase, we don't need to refresh tokens manually
          // Firebase handles token refresh automatically
          // Just handle auth failure (e.g., user disabled)
          if (authFailureHandler) {
            axiosLogger.log('🔧 Calling auth failure handler...');
            await authFailureHandler();
          }
          
          return Promise.reject(error);
        } catch (refreshErr) {
          axiosLogger.error('❌ Auth failure handler failed:', refreshErr);
          // If auth failure handler fails, redirect to login
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
};

export default axiosInstance;
